import { useRef, useCallback, useEffect, useMemo } from 'react';
/**
 * Utility hook to register a single window event listener.
 *
 * The handler will automatically get unregistered on unmount, or when
 * registering a new handler.
 *
 * You can either pass in the event handler directly when calling the hook, or
 * use the register / unregister handle that is returned.
 */
export function useWindowEventListener(eventType, eventHandler) {
    const activeHandler = useRef(null);
    const unregister = useCallback(() => {
        if (activeHandler.current) {
            window.removeEventListener(eventType, activeHandler.current);
            activeHandler.current = null;
        }
    }, [eventType]);
    const register = useCallback((handler) => {
        if (activeHandler.current !== handler) {
            unregister();
            window.addEventListener(eventType, handler);
            activeHandler.current = handler;
        }
    }, [eventType, unregister]);
    useEffect(() => {
        if (eventHandler) {
            register(eventHandler);
        }
    }, [register, eventHandler]);
    useEffect(() => unregister, [unregister]);
    return useMemo(() => ({ register, unregister }), [register, unregister]);
}
